"use client";

import { IconCode } from "@tabler/icons-react";
import Card from "src/components/common/Card/Card";
import Chip from "src/components/common/Chip/Chip";
import CodeBlock from "src/components/common/CodeBlock/CodeBlock";
import FloatingPanel from "src/components/common/FloatingPanel/FloatingPanel";
import IconButton from "src/components/common/IconButton/IconButton";
import Popover from "src/components/common/Popover/Popover";
import Typography from "src/components/common/Typography/Typography";
import DocHeading from "src/components/docs/DocHeading/DocHeading";
import { useExtracted } from "#i18n";

/** An endpoint's error code, as the endpoint card's server component describes it. */
export interface RequestErrorCode {
  code: string;
  status: number;
  message?: string;
  /** The id other pages link to (`getErrorAnchorId`). */
  anchor: string;
}

/**
 * The error codes section of an endpoint card. RequestInfoView resolves the codes on the server and
 * sends plain data, so the error code registry stays there; each code's response body example sits
 * in a click-open popover.
 */
const RequestErrorCodes = (props: { requestId: string; errors: RequestErrorCode[] }) => {
  const translateMessage = useExtracted();
  const { requestId, errors } = props;

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <DocHeading id={`${requestId}-error-codes`} level={4}>
        {translateMessage("Error Codes")}
      </DocHeading>
      <div className="flex flex-col gap-2">
        {errors.map((error) => (
          <Card key={error.code} id={error.anchor} padding="sm" tone="inset" className="text-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Chip color="danger">{error.status}</Chip>
                <Typography as="code" variant="code">
                  {error.code}
                </Typography>
              </div>
              <Popover placement="top" triggers={["click", "keyboard"]}>
                <Popover.Reference asChild={true}>
                  <IconButton
                    type="button"
                    size="sm"
                    variant="outline"
                    label={translateMessage("Show error response body")}
                    labelTooltip={false}
                  >
                    <IconCode size={16} aria-hidden={true} />
                  </IconButton>
                </Popover.Reference>
                <FloatingPanel.Content width="wide">
                  <FloatingPanel.Section padding="form">
                    <div className="flex flex-col gap-2">
                      <div className="font-medium">{translateMessage("Error Response Body:")}</div>
                      <CodeBlock language="json" density="compact" wrap>
                        {JSON.stringify(
                          {
                            code: error.code,
                            message: error.message || translateMessage("An error occurred"),
                            requestId: "...",
                            details: {},
                          },
                          null,
                          2,
                        )}
                      </CodeBlock>
                    </div>
                  </FloatingPanel.Section>
                </FloatingPanel.Content>
              </Popover>
            </div>
            <div className="text-muted">{error.message}</div>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default RequestErrorCodes;
